import { asText } from '@prismicio/helpers';
import { hasRichText } from '@publicss/utils';
import classNames from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import { gsap } from 'gsap';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { PageBodyBlock_FeaturesPrimary } from '../../../types/prismic';
import { usePageContext } from '../../providers/PageContext';
import { media } from '../../styles/media';
import { Button } from '../ui';

const Section = styled.section`
  .image--feature {
    height: 310px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    @media ${media.mdUp} {
      height: 400px;
      padding: 40px;
    }
    @media ${media.lgUp} {
      height: 466px;
      padding: 0;
    }
  }
  .ul--specs {
    font-size: 14px;
    line-height: 160%;
    letter-spacing: 0.01em;
    color: var(--grey-5);
    li + li {
      margin-top: 7px;
    }
    li {
      position: relative;
      padding-inline-start: 20px;
      &::before {
        position: absolute;
        top: 7px;
        inset-inline-start: 0;
        content: '';
        width: 9px;
        height: 9px;
        border-radius: 100%;
        background: var(--primary);
        border: 1px solid var(--primary);
      }

      &.open {
        &::before {
          background: transparent;
        }
      }
    }
    &.color--tertiary {
      li::before {
        background: var(--tertiary);
        border: 1px solid var(--tertiary);
      }
    }
  }
`;

interface BlockFeaturesSliceProps {
  slice: {
    primary: PageBodyBlock_FeaturesPrimary;
  };
}

const BlockFeaturesSlice: React.FunctionComponent<BlockFeaturesSliceProps> = (
  props
) => {
  const { siteTag } = usePageContext();
  const isCK = siteTag === 'site:cloudkitchens';

  const accordion: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const current = e.currentTarget;
    const { id } = current.dataset;
    const accordionContainer = document.querySelector(
      `.accordion[data-id="${id}"]`
    );
    const accordionTrigger = document.querySelector(
      `.accordion--trigger[data-id="${id}"]`
    );
    if (accordionContainer) accordionContainer.classList.toggle('is-active');
    if (accordionTrigger) accordionTrigger.classList.toggle('is-active');
  };

  useEffect(() => {
    const fadeUp = document.querySelectorAll('#block-features .fade-up');

    fadeUp.forEach(function (element) {
      gsap.from(element, {
        scrollTrigger: {
          trigger: element,
          start: 'top 90%',
        },
        duration: 0.5,
        opacity: '0',
        y: '10%',
      });
    });

    const imageFeatureSync = document.querySelector('.image--feature--sync');
    if (imageFeatureSync) {
      const svgSync1 = document.querySelector('#svg--sync-1');
      const svgSync2 = document.querySelector('#svg--sync-2');
      const svgSync3 = document.querySelector('#svg--sync-3');

      const tlSync = gsap.timeline({
        scrollTrigger: {
          trigger: '.image--feature--sync',
          scrub: 0.5,
          start: 'top 90%',
        },
      });

      tlSync.fromTo(svgSync1, { y: '7%' }, { y: '-3%', force3D: true }, 0);
      tlSync.fromTo(svgSync2, { y: '10%' }, { y: '-10%', force3D: true }, 0);
      tlSync.fromTo(svgSync3, { y: '8%' }, { y: '-8%', force3D: true }, 0);
    }

    const imageFeatureDash = document.querySelector('.image--feature--dash');
    if (imageFeatureDash) {
      const svgDash1 = document.querySelector('#svg--dash-1');
      const svgDash2 = document.querySelector('#svg--dash-2');

      const tlDash = gsap.timeline({
        scrollTrigger: {
          trigger: '.image--feature--dash',
          scrub: 0.5,
          start: 'top 90%',
        },
      });

      tlDash.fromTo(svgDash1, { y: '10%' }, { y: '-10%', force3D: true }, 0);
      tlDash.fromTo(svgDash2, { y: '10%' }, { y: '0', force3D: true }, 0);
    }

    const imageFeatureManage = document.querySelector(
      '.image--feature--manage'
    );
    if (imageFeatureManage) {
      const svgManage1 = document.querySelector('#svg--manage');

      const tlManage = gsap.timeline({
        scrollTrigger: {
          trigger: '.image--feature--manage',
          scrub: 0.5,
          start: 'top 90%',
        },
      });

      tlManage.fromTo(svgManage1, { y: '5%' }, { y: '-5%', force3D: true }, 0);
    }

    const imageFeatureMenus = document.querySelector('.image--feature--menus');
    if (imageFeatureMenus) {
      const svgMenus = document.querySelector('#svg--menus');

      const tlMenus = gsap.timeline({
        scrollTrigger: {
          trigger: '.image--feature--menus',
          scrub: 0.5,
          start: 'top 90%',
        },
      });

      tlMenus.fromTo(svgMenus, { y: '5%' }, { y: '-5%', force3D: true }, 0);
    }

    const imageFeatureReports = document.querySelector(
      '.image--feature--reports'
    );
    if (imageFeatureReports) {
      const svgReports1 = document.querySelector('#svg--reports-1');
      const svgReports2 = document.querySelector('#svg--reports-2');

      const tlReports = gsap.timeline({
        scrollTrigger: {
          trigger: '.image--feature--reports',
          scrub: 0.5,
          start: 'top 90%',
        },
      });

      tlReports.fromTo(
        svgReports2,
        { y: '15%' },
        { y: '0%', force3D: true },
        0
      );
      tlReports.fromTo(
        svgReports1,
        { y: '5%' },
        { y: '-5%', force3D: true },
        0
      );
    }

    const imageFeatureHistory = document.querySelector(
      '.image--feature--history'
    );
    if (imageFeatureHistory) {
      const svgHistory = document.querySelector('#svg--history');

      const tlHistory = gsap.timeline({
        scrollTrigger: {
          trigger: '.image--feature--history',
          scrub: 0.5,
          start: 'top 90%',
        },
      });

      tlHistory.fromTo(svgHistory, { y: '10%' }, { y: '0', force3D: true }, 0);
    }
  }, []);

  const { slice } = props;
  const { primary } = slice;
  const {
    specs,
    features,
    eyebrow,
    title,
    description: mainDescription,
    cta_button_link,
    cta_button_text,
  } = primary as PageBodyBlock_FeaturesPrimary & {
    specs: { id: number };
    features: { id: number };
  };

  const queryData = useMergePrismicPreviewData(
    useStaticQuery(graphql`
      {
        allPrismicKitchenSpec {
          edges {
            node {
              _previewable
              prismicId
              data {
                column_title {
                  html
                  richText
                  text
                }
                options {
                  included_for_free
                  option_name {
                    richText
                    html
                    text
                  }
                }
              }
            }
          }
        }
        allPrismicFeature {
          edges {
            node {
              _previewable
              data {
                feature {
                  accordion_description {
                    html
                    richText
                    text
                  }
                  description {
                    html
                    richText
                    text
                  }
                  heading {
                    html
                    text
                    richText
                  }
                  image {
                    url
                  }
                }
              }
              prismicId
            }
          }
        }
      }
    `)
  );

  // The page.js query is filtered by site tag and site lang, so these IDs should be site specific already
  const specsIDS = [specs.id];

  const filteredBlockSpecs = specsIDS.map((id) => {
    return queryData.allPrismicKitchenSpec.edges.find(
      (edge) => edge.node.prismicId === id
    );
  });

  const renderSections = () => {
    if (!filteredBlockSpecs?.[0]) return null;

    const render = [] as any[];
    for (let currentSection = 0; currentSection < 3; currentSection += 1) {
      render.push(
        <div
          className={`col-span-4 lg:col-span-${currentSection === 0 ? 4 : 3} ${
            currentSection === 0 ? 'lg:col-start-2' : ''
          }
        
        `}>
          <ul className="ul--specs color--primary">
            {filteredBlockSpecs[0].node.data.options
              .slice(currentSection * 4, (currentSection + 1) * 4)
              .map((option, index) => {
                return (
                  <li key={index}>{asText(option.option_name.richText)}</li>
                );
              })}
          </ul>
        </div>
      );
    }
    return render.map((Elem) => Elem);
  };

  const featureData = queryData.allPrismicFeature.edges.find(
    (edge) => edge.node.prismicId === features.id
  );

  if (!featureData) return null;

  return (
    <Section id="block-features" className="bg--grey-2">
      <div className="container pb-20 pt-6 md:pb-24 md:pt-0 lg:pb-40 lg:pt-40">
        <div className="grid grid-cols-4 md:grid-cols-9 lg:grid-cols-12">
          <div className="col-span-4 pb-20 md:col-span-9 lg:hidden" />
          <div className="col-span-4 md:col-span-9 lg:col-span-2">
            <div className="eyebrow p5 line color--grey-4 fade-up mb-10 lg:mb-0">
              {asText(eyebrow.richText)}
            </div>
          </div>
          <div className="col-span-4 md:col-span-9 lg:col-span-10">
            <div className="mb-8 grid grid-cols-4 md:grid-cols-9 lg:grid-cols-10">
              <div className="col-span-4 mb-10 md:col-span-5 md:mb-16">
                <h2 className="h2 color--primary fade-up mb-8 md:mb-0">
                  {asText(title.richText)}
                </h2>
              </div>
            </div>
          </div>
          <div className="col-span-4 sm-down:mb-10 lg:col-span-5 lg:col-start-2">
            <div className="grid grid-cols-4 md:grid-cols-5">
              <div className="col-span-4 sm-down:mb-10 lg:col-start-2">
                <p className="fade-up sm-down:mb-2">
                  {asText(mainDescription.richText)}
                </p>
                {cta_button_text?.text && (
                  <Button
                    className="fade-up mt-6 sm-down:w-full"
                    globalTypeform
                    href={cta_button_link?.url}>
                    {cta_button_text?.text}
                  </Button>
                )}
              </div>
            </div>

            {featureData.node.data.feature
              .slice(0, featureData.node.data.feature.length / 2)
              .map((item, i) => {
                const { accordion_description, description, heading, image } =
                  item;
                const hasAccordion = hasRichText(accordion_description);
                return (
                  <>
                    <div className="fade-up mt-10 md:mt-40">
                      <div
                        className={classNames(
                          'image--feature image--feature--dash mb-10',
                          {
                            'bg-[#f2f0e3]': isCK,
                          }
                        )}>
                        <img src={image.url} />
                      </div>
                      <div
                        className={classNames('flex justify-between', {
                          'accordion--trigger': hasAccordion,
                        })}
                        onClick={(e) => hasAccordion && accordion(e)}
                        data-id={`first-${i}`}>
                        <div className="flex flex-col lg:flex-row lg:gap-14">
                          <p className="color--grey-6 h6 mb-2">
                            {asText(heading.richText)}
                          </p>
                          <p className="small color--grey-5 w-25 mb-0 lg:pr-4">
                            {asText(description.richText)}
                          </p>
                        </div>
                        {hasAccordion && (
                          <div className="ml-auto">
                            <svg
                              width="26"
                              height="26"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg">
                              <rect
                                x=".5"
                                y=".5"
                                width="25"
                                height="25"
                                rx="12.5"
                                stroke="#B5B3AB"
                              />
                              <path
                                d="M16.664 12.168h-3.168V9h-1.344v3.168H9v1.344h3.152v3.152h1.344v-3.152h3.168v-1.344z"
                                fill="#B5B3AB"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="accordion" data-id={`first-${i}`}>
                        <div className="bb pb-4 pr-10 pt-6">
                          <p className="small color--grey-5">
                            {asText(accordion_description.richText)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}
          </div>

          <div className="col-span-4 md:col-start-6 lg:col-span-5 lg:col-start-8">
            {featureData.node.data.feature
              .slice(
                featureData.node.data.feature.length / 2,
                featureData.node.data.feature.length
              )
              .map((item, i) => {
                const { accordion_description, description, heading, image } =
                  item;
                const hasAccordion = hasRichText(accordion_description);
                return (
                  <>
                    <div className={`fade-up ${i > 0 ? 'md:mt-40' : ''} `}>
                      <div
                        className={classNames(
                          'image--feature image--feature--dash mb-10',
                          {
                            'bg-[#f2f0e3]': isCK,
                          }
                        )}>
                        <img src={image.url} />
                      </div>
                      <div
                        className={classNames('flex justify-between', {
                          'accordion--trigger': hasAccordion,
                        })}
                        onClick={(e) => hasAccordion && accordion(e)}
                        data-id={`second-${i}`}>
                        <div className="mb-8 flex flex-col md:mb-0 lg:flex-row lg:gap-14">
                          <p className="color--grey-6 h6 mb-2">
                            {asText(heading.richText)}
                          </p>
                          <p className="small color--grey-5 w-25 mb-0 lg:pr-4">
                            {asText(description.richText)}
                          </p>
                        </div>
                        {hasAccordion && (
                          <div className="ml-auto">
                            <svg
                              width="26"
                              height="26"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg">
                              <rect
                                x=".5"
                                y=".5"
                                width="25"
                                height="25"
                                rx="12.5"
                                stroke="#B5B3AB"
                              />
                              <path
                                d="M16.664 12.168h-3.168V9h-1.344v3.168H9v1.344h3.152v3.152h1.344v-3.152h3.168v-1.344z"
                                fill="#B5B3AB"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="accordion" data-id={`second-${i}`}>
                        <div className="bb pb-4 pr-10 pt-6">
                          <p className="small color--grey-5">
                            {asText(accordion_description.richText)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}
          </div>
        </div>
        <div className="fade-up grid grid-cols-4 md:grid-cols-9 lg:grid-cols-12">
          <div className="col-span-4 mt-20 md:col-span-9 md:mt-32 lg:col-span-11 lg:col-start-2">
            <h3 className="h5 mb-6">
              {asText(filteredBlockSpecs[0].node.data.column_title.richText)}
            </h3>
          </div>
          {renderSections()}
        </div>
      </div>
    </Section>
  );
};

export default BlockFeaturesSlice;
